import { ResponsiveLine, LineSvgProps } from '@nivo/line';
import { LAPTOP_MAX_SIZE, useWindowSize } from '@sendible/design-system';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { getChartLeftMargin } from '@sendible/frontendv2/src/pages/Reports/helpers';
import { makeNumbersReadFriendly } from '@sendible/frontendv2/src/utils';
import { ChartContainer } from '../../index.styles';
import { Tooltip } from '../../../Audience/components/Tooltip';
import { useTikTokReportContext } from '../../../../context';

type ChartSeries = NonNullable<
  LineSvgProps<{
    id: string | number;
    data: Array<{ x: string | number; y: string | number | null }>;
  }>['data']
>[number];

interface VideosViewsByDayChartProps {
  data: ChartSeries[];
  isBlank?: boolean;
}

export const VideosViewsByDayChart = ({ data, isBlank }: VideosViewsByDayChartProps) => {
  const { userOffset } = useTikTokReportContext();
  const { width } = useWindowSize();

  const isSmallScreen = width < LAPTOP_MAX_SIZE;
  const maxLegends = 7;
  const isBigList = data[0]?.data ? data[0].data.length > maxLegends : false;

  const [
    {
      user: { language },
    },
  ] = useBridgeContext();

  const getDateValue = (string: string) => {
    const dt = new Date(string);

    dt.setHours(dt.getHours() + userOffset / 60);

    return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(dt);
  };

  const mappedDataNumbers = data[0]?.data ? data[0].data.map(({ y }) => Number(y ?? 0)) : [];

  const biggestValue = mappedDataNumbers.length ? Math.max(...mappedDataNumbers) : 0;
  const smallestValue = mappedDataNumbers.length ? Math.min(...mappedDataNumbers) : 0;
  const marginLeft = getChartLeftMargin(biggestValue, smallestValue);

  return (
    <ChartContainer>
      <ResponsiveLine
        data={data}
        margin={{
          top: isSmallScreen ? 50 : 20,
          right: isSmallScreen ? 24 : 120,
          bottom: 24,
          left: isBlank ? 28 : marginLeft,
        }}
        xScale={{
          type: 'point',
        }}
        yScale={{
          type: 'linear',
          min: 0,
          max: isBlank ? 200 : 'auto',
          clamp: true,
          stacked: false,
          reverse: false,
        }}
        yFormat=" >-.2f"
        axisBottom={{
          tickValues: data[0]?.data
            ? data[0].data.filter((_: unknown, index: number) => (isBigList ? index % maxLegends === 0 : true)).map(({ x }) => String(x))
            : [],
          format: (e) => getDateValue(e),
        }}
        axisLeft={{
          format: (v) => makeNumbersReadFriendly(v),
        }}
        animate={false}
        enableGridX={false}
        enableGridY
        colors={['#000000']}
        lineWidth={isBigList ? 2 : 4}
        enablePoints={false}
        enableArea
        enableCrosshair={false}
        useMesh
        tooltip={Tooltip}
        legends={
          !isSmallScreen
            ? [
                {
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 100,
                  translateY: 0,
                  itemsSpacing: 2,
                  itemDirection: 'left-to-right',
                  itemWidth: 80,
                  itemHeight: 20,
                  itemOpacity: 0.75,
                  symbolSize: 12,
                  symbolShape: 'circle',
                },
              ]
            : [
                {
                  anchor: 'top-left',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: -32,
                  itemsSpacing: 2,
                  itemDirection: 'left-to-right',
                  itemWidth: 100,
                  itemHeight: 20,
                  itemOpacity: 0.75,
                  symbolSize: 12,
                  symbolShape: 'circle',
                },
              ]
        }
      />
    </ChartContainer>
  );
};
